import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, X } from 'lucide-react'
import { fetchPostureAttention, fetchPostureDevices } from '@/api/charts'
import { SimplePieChart } from '@/components/charts/SimplePieChart'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { PostureLink, TrafficLightDot } from '@/components/PostureIndicator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  exportPostureAttentionCsv,
  exportPostureDrillDownCsv,
} from '@/lib/exportDashboardDrillDown'
import { TRAFFIC } from '@/lib/trafficLight'
import { cn } from '@/lib/utils'

const STATUS_LABELS = {
  green: 'OK',
  amber: 'Review',
  red: 'Action needed',
  grey: 'No data',
}

const STATUS_COLOURS = {
  green: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
  grey: '#94a3b8',
}

const LABEL_TO_STATUS = Object.fromEntries(
  Object.entries(STATUS_LABELS).map(([status, label]) => [label, status]),
)

const AREA_LABELS = {
  security: 'Security',
  hardware: 'Hardware',
}

function overallFromCounts(counts) {
  if (!counts) return 'grey'
  if (counts.red > 0) return 'red'
  if (counts.amber > 0) return 'amber'
  if (counts.green > 0) return 'green'
  return 'grey'
}

function headlineForCounts(counts, label) {
  if (!counts) return `No ${label} data yet`
  if (counts.red > 0) {
    return `${counts.red} device${counts.red === 1 ? '' : 's'} need ${label} action`
  }
  if (counts.amber > 0) {
    return `${counts.amber} device${counts.amber === 1 ? '' : 's'} need ${label} review`
  }
  if (counts.green > 0) {
    return `All assessed devices look good for ${label}`
  }
  return `No ${label} data yet`
}

function countsToPieData(counts) {
  return ['green', 'amber', 'red', 'grey']
    .map((status) => ({
      label: STATUS_LABELS[status],
      value: Number(counts?.[status] || 0),
      status,
    }))
    .filter((row) => row.value > 0)
}

function PosturePieCard({ area, title, counts, selectedFilter, onSelectFilter }) {
  const overall = overallFromCounts(counts)
  const traffic = TRAFFIC[overall] ?? TRAFFIC.grey
  const headline = headlineForCounts(counts, title.toLowerCase())
  const pieData = useMemo(() => countsToPieData(counts), [counts])
  const total = ['green', 'amber', 'red', 'grey'].reduce(
    (sum, key) => sum + Number(counts?.[key] || 0),
    0,
  )
  const activeLabel =
    selectedFilter?.area === area ? STATUS_LABELS[selectedFilter.status] : null

  function handleSliceClick(label) {
    const status = LABEL_TO_STATUS[label]
    if (!status) return
    onSelectFilter?.({ area, status })
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <TrafficLightDot status={overall} className="h-4 w-4" />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base">{title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{headline}</p>
          </div>
          {overall !== 'grey' && (
            <Badge variant={traffic.badge} className="shrink-0">
              {traffic.label}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <SimplePieChart
          data={pieData}
          emptyMessage={`No ${title} data yet`}
          activeLabel={activeLabel}
          onItemClick={onSelectFilter ? handleSliceClick : undefined}
          colourFor={(entry) => STATUS_COLOURS[entry.status] || STATUS_COLOURS.grey}
          heightClassName="h-[180px]"
        />
        <p className="text-xs text-muted-foreground">
          {total} device{total === 1 ? '' : 's'} in scope · click a slice or legend to list
          devices
        </p>
      </CardContent>
    </Card>
  )
}

export function EstatePostureSummary({ data, loading, selectedFilter, onSelectFilter }) {
  if (loading) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <PosturePieCard
        area="security"
        title="Security"
        counts={data.security}
        selectedFilter={selectedFilter}
        onSelectFilter={onSelectFilter}
      />
      <PosturePieCard
        area="hardware"
        title="Hardware"
        counts={data.hardware}
        selectedFilter={selectedFilter}
        onSelectFilter={onSelectFilter}
      />
    </div>
  )
}

/** ~10 body rows (+ header) before vertical scroll. */
const DEVICE_TABLE_SCROLL_CLASS = 'max-h-[32.5rem] overflow-y-auto'

function PostureDeviceTable({ devices, showCustomer = false, showReasons = false }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className={DEVICE_TABLE_SCROLL_CLASS}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-border bg-muted text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Device</th>
              {showCustomer && <th className="px-4 py-3 font-medium">Customer</th>}
              {showReasons && <th className="px-4 py-3 font-medium">Reasons</th>}
              <th className="px-4 py-3 font-medium">Security</th>
              <th className="px-4 py-3 font-medium">Hardware</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <tr key={device.agent_id} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-3">
                  <Link
                    to={`/agents/${device.agent_id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {device.hostname || device.display_name || `Device ${device.agent_id}`}
                  </Link>
                  {device.display_name && device.hostname && (
                    <p className="text-xs text-muted-foreground">{device.display_name}</p>
                  )}
                </td>
                {showCustomer && (
                  <td className="px-4 py-3 text-muted-foreground">
                    {device.customer_name || '—'}
                  </td>
                )}
                {showReasons && (
                  <td className="px-4 py-3 text-muted-foreground">
                    {(device.reasons || []).length
                      ? device.reasons.map((r) => r.label).join(', ')
                      : '—'}
                  </td>
                )}
                <td className="px-4 py-3">
                  <PostureLink
                    agentId={device.agent_id}
                    status={device.security_status}
                    tab="security"
                    label="Security"
                  />
                </td>
                <td className="px-4 py-3">
                  <PostureLink
                    agentId={device.agent_id}
                    status={device.hardware_status}
                    tab="hardware"
                    label="Hardware"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PostureReasonBreakdown({
  reasonCounts,
  selectedReason,
  onSelectReason,
  loading,
}) {
  if (!reasonCounts?.length && !selectedReason) return null

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Reasons
        </p>
        {selectedReason ? (
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => onSelectReason(null)}
          >
            All reasons
          </button>
        ) : null}
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
              <th className="px-4 py-2 font-medium">Reason</th>
              <th className="px-4 py-2 font-medium text-right tabular-nums">Devices</th>
            </tr>
          </thead>
          <tbody>
            {(reasonCounts || []).map((row) => {
              const active = selectedReason === row.id
              return (
                <tr key={row.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => onSelectReason(active ? null : row.id)}
                      className={cn(
                        'text-left hover:underline',
                        active && 'font-semibold text-primary',
                      )}
                    >
                      {row.label}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">
                    {row.count}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        A device can appear under more than one reason. Click a reason to filter the list.
      </p>
    </div>
  )
}

export function PostureDrillDown({
  filter,
  customerIds,
  showCustomer = false,
  onClose,
}) {
  const [page, setPage] = useState(1)
  const [reason, setReason] = useState(null)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')
  const filterKey = filter ? `${filter.area}:${filter.status}` : ''
  const customerKey = Array.isArray(customerIds) ? customerIds.join(',') : ''

  useEffect(() => {
    setPage(1)
    setReason(null)
    setResult(null)
    setExportError('')
  }, [filterKey, customerKey])

  useEffect(() => {
    if (!filter) {
      setResult(null)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    fetchPostureDevices(customerIds, filter.area, filter.status, page, 50, reason)
      .then((data) => {
        if (!cancelled) setResult(data)
      })
      .catch(() => {
        if (!cancelled) setResult(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [filter, customerIds, page, reason])

  function handleSelectReason(next) {
    setPage(1)
    setReason(next)
    setExportError('')
  }

  if (!filter) return null

  const areaLabel = AREA_LABELS[filter.area] ?? filter.area
  const statusLabel = STATUS_LABELS[filter.status] ?? filter.status
  const traffic = TRAFFIC[filter.status] ?? TRAFFIC.grey
  const showReasons = filter.status === 'red' || filter.status === 'amber'
  const reasonCounts = result?.reason_counts ?? []
  const reasonLabel =
    reason && (reasonCounts.find((r) => r.id === reason)?.label || reason)

  async function handleExport() {
    setExporting(true)
    setExportError('')
    try {
      await exportPostureDrillDownCsv({
        customerIds,
        area: filter.area,
        status: filter.status,
        reason,
        reasonLabel,
        showCustomer,
      })
    } catch {
      setExportError('Could not export this list.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <TrafficLightDot status={filter.status} className="h-3 w-3" />
            <CardTitle className="text-base">
              {areaLabel} — {statusLabel}
            </CardTitle>
            <Badge variant={traffic.badge}>{traffic.label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {loading
              ? 'Loading devices…'
              : result
                ? `${result.total} device${result.total === 1 ? '' : 's'}${
                    reason
                      ? ` matching “${reasonLabel}”`
                      : ' in this category'
                  }`
                : 'Could not load devices.'}
          </p>
          {exportError ? (
            <p className="text-xs text-destructive">{exportError}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || loading || !result?.total}
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close list">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showReasons && (reasonCounts.length > 0 || reason) ? (
          <PostureReasonBreakdown
            reasonCounts={reasonCounts}
            selectedReason={reason}
            onSelectReason={handleSelectReason}
            loading={loading}
          />
        ) : null}

        {loading && !result ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !result?.items?.length ? (
          <p className="text-sm text-muted-foreground">No devices in this category.</p>
        ) : (
          <>
            <PostureDeviceTable
              devices={result.items}
              showCustomer={showCustomer}
              showReasons={showReasons}
            />
            {result.pages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Page {result.page} of {result.pages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= result.pages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function DevicesNeedingAttention({
  customerIds = [],
  loading: summaryLoading,
  showCustomer = false,
  total = null,
  reasonCounts = null,
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState(null)
  const [page, setPage] = useState(1)
  const [result, setResult] = useState(null)
  const [listLoading, setListLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')
  const count = typeof total === 'number' ? total : 0
  const customerKey = Array.isArray(customerIds) ? customerIds.join(',') : ''
  const stableReasonCounts = reasonCounts?.length
    ? reasonCounts
    : result?.reason_counts || []
  const collapsedSummary = summaryLoading
    ? 'Loading…'
    : count === 0
      ? 'No devices flagged for review or action'
      : `${count} device${count === 1 ? '' : 's'} flagged for review or action`

  useEffect(() => {
    setPage(1)
    setReason(null)
    setResult(null)
    setExportError('')
  }, [customerKey])

  useEffect(() => {
    if (!open || count === 0) {
      if (!open) setResult(null)
      return undefined
    }
    let cancelled = false
    setListLoading(true)
    fetchPostureAttention(customerIds, page, 50, reason)
      .then((data) => {
        if (!cancelled) setResult(data)
      })
      .catch(() => {
        if (!cancelled) setResult(null)
      })
      .finally(() => {
        if (!cancelled) setListLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, customerIds, page, reason, count])

  function handleSelectReason(next) {
    setPage(1)
    setReason(next)
    setExportError('')
  }

  const listTotal = result?.total
  const reasonLabel =
    reason &&
    (stableReasonCounts.find((r) => r.id === reason)?.label || reason)

  async function handleExport() {
    setExporting(true)
    setExportError('')
    try {
      await exportPostureAttentionCsv({
        customerIds,
        reason,
        reasonLabel,
        showCustomer,
      })
    } catch {
      setExportError('Could not export this list.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <CollapsePanel
      id="devices-needing-attention"
      title="Devices needing attention"
      collapsedSummary={collapsedSummary}
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setReason(null)
          setPage(1)
          setResult(null)
          setExportError('')
        }
      }}
      pill={summaryLoading ? undefined : String(count)}
      pillVariant={count > 0 ? 'pending' : 'inactive'}
      bodyClassName="space-y-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {listLoading
            ? 'Loading devices…'
            : typeof listTotal === 'number'
              ? `${listTotal} device${listTotal === 1 ? '' : 's'}${
                  reason ? ` matching “${reasonLabel}”` : ' flagged for review or action'
                }. Click a reason to filter; click again to clear.`
              : 'Devices with security or hardware flagged for review or action. Click a reason to filter the full list.'}
        </p>
        {count > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={handleExport}
            disabled={exporting || listLoading || !listTotal}
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        ) : null}
      </div>
      {exportError ? (
        <p className="text-xs text-destructive">{exportError}</p>
      ) : null}
      {summaryLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : count === 0 ? (
        <p className="text-sm text-muted-foreground">
          No devices flagged for review or action in this scope.
        </p>
      ) : (
        <>
          {(stableReasonCounts.length > 0 || reason) && (
            <PostureReasonBreakdown
              reasonCounts={stableReasonCounts}
              selectedReason={reason}
              onSelectReason={handleSelectReason}
              loading={listLoading}
            />
          )}
          {listLoading && !result ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !result?.items?.length ? (
            <p className="text-sm text-muted-foreground">
              {reason
                ? 'No devices match that reason in the current scope.'
                : 'No devices flagged for review or action in this scope.'}
            </p>
          ) : (
            <>
              <PostureDeviceTable
                devices={result.items}
                showCustomer={showCustomer}
                showReasons
              />
              {result.pages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {result.page} of {result.pages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1 || listLoading}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= result.pages || listLoading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </CollapsePanel>
  )
}
